import { useState, useEffect, useRef } from "react";
import { api } from "../../lib/api";
import { useCardExpand } from "../../lib/cardExpand";
import { ExpandButton } from "./CardModal";

type Message = { role: "user" | "assistant"; content: string };

type Props = { firstName: string; _expanded?: boolean };

const QUICK_REPLIES = [
  { emoji: "😊", text: "Log my mood" },
  { emoji: "😰", text: "I feel stressed" },
  { emoji: "⚡", text: "Log my energy" },
  { emoji: "😴", text: "I slept poorly" },
  { emoji: "🔥", text: "Motivate me" },
];

export function AICoachCard({ firstName, _expanded }: Props) {
  const { expand } = useCardExpand();
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: `Good morning, ${firstName}. 🌿 I'm your LifeScribe coach. Tell me how you're feeling, log a mood, or ask about your plan.`,
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const msgsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [messages]);

  const send = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput("");
    setSending(true);

    const newMessages: Message[] = [...messages, { role: "user", content }];
    setMessages(newMessages);

    try {
      const { reply } = await api.coach.chat({ messages: newMessages });
      setMessages([...newMessages, { role: "assistant", content: reply }]);
    } catch (e: any) {
      setMessages([
        ...newMessages,
        { role: "assistant", content: "I'm having trouble connecting right now. Try again in a moment, or jot it in your journal." },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={_expanded ? "flex flex-col h-full" : "bg-d_card border border-d_border/20 rounded-[10px] p-3 transition-colors hover:border-d_celeste/10 h-full min-h-0 overflow-hidden flex flex-col"}>
      {!_expanded && (
        <div className="font-serif text-[17px] font-semibold mb-2.5 flex items-center justify-between tracking-[0.01em]">
          AI Wellness Coach
          <ExpandButton onClick={() => expand({ title: "AI Wellness Coach", render: () => <AICoachCard firstName={firstName} _expanded /> })} />
        </div>
      )}

      <div className="flex gap-1.5 mb-2 flex-wrap">
        {QUICK_REPLIES.map((q) => (
          <button
            key={q.text}
            onClick={() => send(q.text)}
            disabled={sending}
            className="text-[9px] font-medium px-2 py-1 rounded-md border border-d_border/20 bg-d_card2 hover:border-d_border_h text-d_text2 disabled:opacity-40 transition"
          >
            {q.emoji} {q.text.split(" ")[1] ?? q.text.split(" ")[0]}
          </button>
        ))}
      </div>

      <div ref={msgsRef} className="flex-1 overflow-y-auto space-y-1.5 min-h-0 pr-1 mb-2">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`max-w-[85%] text-[11px] leading-relaxed px-2.5 py-1.5 rounded-lg ${
              m.role === "user"
                ? "bg-d_accent/15 text-d_text border border-d_accent/20 ml-auto"
                : "bg-d_card2 text-d_text2 border border-d_border/20"
            }`}
          >
            {m.content}
          </div>
        ))}
        {sending && (
          <div className="bg-d_card2 text-d_text3 border border-d_border/20 max-w-[85%] text-[11px] px-2.5 py-1.5 rounded-lg">
            <span className="inline-block pulse-soft">●</span>
            <span className="inline-block pulse-soft mx-1" style={{ animationDelay: "0.2s" }}>●</span>
            <span className="inline-block pulse-soft" style={{ animationDelay: "0.4s" }}>●</span>
          </div>
        )}
      </div>

      <div className="flex gap-1.5">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          disabled={sending}
          placeholder="How are you feeling…"
          className="flex-1 bg-d_card2 border border-d_border/20 rounded-md px-2.5 py-1.5 text-[11px] focus:border-d_accent focus:outline-none transition disabled:opacity-50 text-d_text"
        />
        <button
          onClick={() => send()}
          disabled={sending || !input.trim()}
          className="text-[10px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-md bg-d_accent text-black disabled:opacity-40 disabled:bg-d_accent/40 hover:bg-d_accent/90 transition"
        >
          Send
        </button>
      </div>
    </div>
  );
}
